import ENV from "@/utils/env";
import { api } from "@/lib/api";

/* Request Types */
export interface UploadFileReq {
  key: string;
  file: File;
  url: string;
  onProgress?: (percent: number) => void;
}

/* Response Types */
export interface StageUploadResp {
  key: string;
  url: string;
}

export interface StageContextUploadResp {
  id: string;
  url: string;
}

export interface StudyPreProcessResp {
  taskIds: string[];
}

export interface StudyAssetStatus {
  assetId: string;
  filename: string;
  status: "RUNNING" | "SUCCEEDED" | "FAILED";
}

/**
 * stageUpload
 *
 * Generates a pre-signed URL to a file in S3.  The URL is valid for one upload and has an expiration.
 * Uses POST instead of GET to avoid Safari's aggressive caching of GET requests.
 *
 * @returns A fully qualified, pre-signed URL.
 */
export const stageUpload = async (projectId: string, studyId: string) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/uploads/stage`);
  return resp.data as StageUploadResp;
};

/**
 * stageVideoUpload
 *
 * Generates a pre-signed URL for a video upload via the Connect API (df-pipelines).
 * Videos are staged through Connect rather than the image path, so the file lands in the
 * bucket the video pipeline reads from.
 *
 * @param projectId - The project the study belongs to
 * @param studyId - The study the video is being uploaded to
 * @returns The staged asset key and its fully qualified, pre-signed URL.
 */
export const stageVideoUpload = async (projectId: string, studyId: string) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/uploads/videos/stage`);
  return resp.data as StageUploadResp;
};

/**
 * uploadUpload
 *
 * Upload a file to S3 using the pre-signed URL returned by stageFile.
 *
 * @returns It returns the key of the uploaded file.
 */
export const uploadUpload = async ({ key, file, url, onProgress }: UploadFileReq) => {
  await api.put(url, file, {
    headers: { "Content-Type": file.type },
    onUploadProgress: (event) => {
      onProgress?.(Math.round((event.loaded / (event.total ?? file.size)) * 100));
    },
  });

  return key;
};

/**
 * studyPreProcess
 *
 * Triggers study-level preprocessing for all assets in a study.
 * Assets are already uploaded at the select step, so no request body is needed.
 */
export const studyPreProcess = async (projectId: string, studyId: string): Promise<StudyPreProcessResp> => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/preprocess`);
  return resp.data as StudyPreProcessResp;
};

/**
 * studyProcess
 *
 * Triggers study-level processing for all assets in a study.
 * No request body needed, just project and study IDs.
 */
export const studyProcess = async (projectId: string, studyId: string): Promise<StudyPreProcessResp> => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/process`);
  return resp.data as StudyPreProcessResp;
};

/**
 * getStudyStatus
 *
 * Polls the status of all assets in a study for a given process type.
 * Returns an array of assets with their current status.
 */
export const getStudyStatus = async (projectId: string, studyId: string, processType: "preprocess" | "process"): Promise<StudyAssetStatus[]> => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/status`, {
    processType,
  });
  return resp.data as StudyAssetStatus[];
};

/*
 * stageContextUpload
 *
 * Generates a pre-signed URL to a file in S3.  The URL is valid for one context upload and has an expiration.
 */
export const stageContextUpload = async (contextPackId: string) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/contexts/stage`, { packId: contextPackId });
  return resp.data as StageContextUploadResp;
};
